import { useOptionalRoundRoute } from "@client/RoundRouteContext";
import { useEffectiveUser } from "@client/hooks/use-effective-user";
import { isIPadOfflineFailure } from "@client/offline/offlineCache";
import { type RoundGroup } from "@lib/roundGroups";
import { trpc } from "@client/trpc";
import {
  AppBar,
  type AppBarProps,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  cn,
} from "@sfc/ui";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";

/** Round navigation alongside the existing app switcher. */
export function RoundSwitcherAppBar(props: AppBarProps) {
  const route = useOptionalRoundRoute();
  const { data: viewer } = useEffectiveUser();
  const funderPath = `/round/${route?.reference}/funder`;
  const isFunderPage =
    !!route &&
    (props.pathname === funderPath ||
      props.pathname?.startsWith(`${funderPath}/`) === true);
  // Fetch before opening: membership determines whether a picker exists at all.
  // The viewer is part of the key, including in the iPad's persisted query cache.
  const query = trpc.getRoundGroups.useQuery(
    { viewerId: viewer?.userId ?? "" },
    { enabled: isFunderPage && !!viewer },
  );
  const failure = query.error ?? query.failureReason;
  const groups =
    failure && !isIPadOfflineFailure(failure) ? undefined : query.data;
  const group = isFunderPage
    ? groups?.find((item) =>
        item.rounds.some((round) => round.id === route?.roundId),
      )
    : undefined;
  return (
    <AppBar
      {...props}
      titleControl={
        route ? (
          <ContextPicker
            key={`${viewer?.userId}:${group?.id}:${route.roundId}`}
            group={group}
            roundId={route.roundId}
            workspacePath={props.pathname?.slice(funderPath.length) ?? ""}
            title={props.title}
            apps={props.apps}
            currentAppKey={props.currentAppKey}
            link={props.link}
          />
        ) : undefined
      }
    />
  );
}

function ContextLabel({ title }: { title: string }) {
  return (
    <span className="flex min-w-0 flex-col items-start gap-0.5 text-left">
      <span className="text-base leading-5 font-semibold">S-Process</span>
      <Text
        as="span"
        variant="label-sm"
        tone="muted-foreground"
        className="max-w-32 truncate sm:max-w-48"
        title={title}
      >
        {title}
      </Text>
    </span>
  );
}

function ContextPicker({
  group,
  roundId,
  workspacePath,
  title,
  apps = [],
  currentAppKey,
  link: Link,
}: Pick<AppBarProps, "apps" | "currentAppKey" | "link"> & {
  group?: RoundGroup;
  roundId: string;
  workspacePath: string;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const rounds = [...(group?.rounds ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const hasApps = apps.length > 1;

  if (!group && !hasApps) {
    return <ContextLabel title={title} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="nav"
            className="h-auto min-h-11 min-w-0 gap-2 rounded-md px-1.5 py-1"
            aria-label={group ? "Switch round" : "S-Process, switch app"}
          />
        }
      >
        <ContextLabel title={title} />
        <ChevronDownIcon className="size-3.5 shrink-0 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-60 max-w-[calc(100vw-2rem)] p-1"
        aria-label={group ? "Switch round" : "Switch app"}
        instantClose
      >
        {group && (
          <div className="max-h-64 overflow-y-auto overscroll-contain">
            {rounds.map((round) => (
              <Button
                key={round.id}
                variant="nav"
                size="sm"
                className={cn(
                  "h-auto min-h-9 w-full justify-between gap-2 rounded px-2 py-1.5 text-left whitespace-normal [@media(pointer:coarse)]:min-h-11",
                  round.id === roundId && "bg-surface-elevated",
                )}
                onClick={() => {
                  setOpen(false);

                  if (round.id !== roundId) {
                    // Keep the section/subpage, but leave round-specific item IDs,
                    // media selections, and search parameters behind.
                    const pagePath = workspacePath
                      .split(/[?#]/, 1)[0]!
                      .split("/")
                      .slice(0, 3)
                      .join("/");
                    navigate(`~/round/${round.id}/funder${pagePath}`);
                  }
                }}
                aria-current={round.id === roundId ? "true" : undefined}
              >
                <Text as="span" variant="body-sm" className="min-w-0">
                  {round.name}
                </Text>
                {round.id === roundId && (
                  <CheckIcon className="size-3.5 shrink-0 text-accent" />
                )}
              </Button>
            ))}
          </div>
        )}
        {hasApps && (
          <div
            aria-label="Apps"
            className={cn(
              "flex gap-1",
              group && "mt-1 border-t border-border pt-1",
            )}
          >
            {apps.map((app) => (
              <Button
                key={app.key}
                variant="nav"
                size="sm"
                nativeButton={false}
                render={
                  app.reloadDocument ? (
                    <a href={app.href} />
                  ) : (
                    <Link href={app.href} />
                  )
                }
                onClick={() => setOpen(false)}
                className={cn(
                  "h-auto min-h-11 min-w-0 flex-1 flex-col gap-1 rounded px-1 py-2 text-xs",
                  app.key === currentAppKey &&
                    "bg-surface-elevated text-accent",
                )}
                aria-current={app.key === currentAppKey ? "true" : undefined}
              >
                <app.icon className="size-4 shrink-0" />
                <span>{app.label}</span>
              </Button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
